// The takeoff FILE: export a revision's takeoff to a JSON file and import one into a revision (pure — the page does
// the I/O; the database does the replace in one transaction through sp_ImportTakeoffDocument).
//
// What is in it: the revision's rooms, drawn conditions, edge overrides and each drawing's scale / offset / page /
// opacity (the same document a snapshot holds — db/85 fn_TakeoffDocument), plus a list of the drawings it was taken
// off (name, number, file name and SHA-256) so an import can find the same drawings in another revision. It does NOT
// carry the drawing files (the project archive does), criteria, room elements or equipment.
//
// Importing REPLACES the target revision's takeoff (an automatic snapshot of what is replaced is saved first, and a
// locked revision is refused). Three things make that safe, all done here before anything is sent:
//   1. room ids are renumbered to ones that cannot exist in the database, so a room is never matched to a DIFFERENT
//      room by an id collision (the restore would otherwise update it in place);
//   2. the file's drawings are mapped onto the target revision's sheets (by file hash, then name; the person can fix
//      or skip each), and rows on a drawing that is skipped are dropped — never left sheet-less, which would show
//      them on every drawing;
//   3. references that only mean something inside the SOURCE revision are removed and reported (AHU zone
//      assignments; back-outs, which hang off room elements the file does not carry).

export const FILE_FORMAT = "cleanspace-takeoff";
export const FILE_VERSION = 1;
export const FILE_EXTENSION = ".cstakeoff.json";
export const MAX_FILE_CHARS = 25 * 1024 * 1024;
export const MAX_ROOMS = 5000;
export const MAX_CONDITIONS = 100000;

const isObj = (v) => v != null && typeof v === "object" && !Array.isArray(v);
const asDoc = (d) => (typeof d === "string" ? JSON.parse(d) : d);

// ── export ───────────────────────────────────────────────────────────────────
/**
 * The file object.
 *   document   what sp_GetTakeoffDocument returned (JSON text or object)
 *   sheets     the revision's drawing rows as loaded (their file bytes are NOT read)
 */
export function buildTakeoffFile({ document, sheets = [], project = "", revision = "", revisionId = null, host = "", exportedBy = "", exportedAt = new Date() }) {
  const takeoff = asDoc(document);
  if (!isObj(takeoff) || !Array.isArray(takeoff.rooms)) throw new Error("The takeoff document is missing.");
  return {
    format: FILE_FORMAT,
    version: FILE_VERSION,
    exportedAt: new Date(exportedAt).toISOString(),
    exportedBy: exportedBy || null,
    source: { project, revision, revisionId, host },
    drawings: sheets.map(s => ({
      sheetId: s.SheetID, name: s.SheetName || "", number: s.SheetNumber || "", discipline: s.Discipline || "",
      fileName: s.FileName || "", fileHash: s.FileHash || "", isPdf: !!s.ImageIsPdf, page: s.ImagePage || 1,
    })),
    counts: { rooms: takeoff.rooms.length, conditions: takeoff.conditions?.length ?? 0, overrides: takeoff.overrides?.length ?? 0, drawings: sheets.length },
    takeoff,
  };
}

export const serializeTakeoffFile = (file) => JSON.stringify(file, null, 1);

export function takeoffFileName({ project = "", revision = "", date = new Date() } = {}) {
  const stamp = new Date(date).toISOString().slice(0, 10);
  const base = [project, revision, "takeoff", stamp].filter(Boolean).join("_").replace(/[^A-Za-z0-9._-]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
  return base + FILE_EXTENSION;
}

// ── read ─────────────────────────────────────────────────────────────────────
/** Parse and check a file's text. -> { ok: true, file, warnings } | { ok: false, error } — never throws. */
export function parseTakeoffFile(text) {
  if (typeof text !== "string" || !text.trim()) return { ok: false, error: "The file is empty." };
  if (text.length > MAX_FILE_CHARS) return { ok: false, error: `The file is larger than an import accepts (${Math.round(MAX_FILE_CHARS / 1048576)} MB).` };
  let file;
  try { file = JSON.parse(text); } catch { return { ok: false, error: "That is not a takeoff file (it is not valid JSON)." }; }
  if (!isObj(file) || file.format !== FILE_FORMAT) return { ok: false, error: "That is not a CleanSpace takeoff file." };
  if (!Number.isInteger(file.version) || file.version < 1) return { ok: false, error: "The file has no version." };
  if (file.version > FILE_VERSION) return { ok: false, error: `The file was made by a newer version of the app (file version ${file.version}); update before importing it.` };
  const t = file.takeoff;
  if (!isObj(t) || !Array.isArray(t.rooms) || !Array.isArray(t.conditions) || !Array.isArray(t.overrides) || !Array.isArray(t.sheets)) {
    return { ok: false, error: "The file is missing its takeoff (rooms, conditions, edge overrides and drawing setup)." };
  }
  if (t.rooms.length > MAX_ROOMS || t.conditions.length > MAX_CONDITIONS) return { ok: false, error: "The file holds more than an import accepts." };
  if (!t.rooms.every(r => isObj(r) && Number.isInteger(r.RoomID))) return { ok: false, error: "A room in the file has no valid id." };
  if (!t.conditions.every(isObj) || !t.overrides.every(isObj) || !t.sheets.every(isObj)) return { ok: false, error: "The file contains a malformed record." };
  const warnings = [];
  const drawings = Array.isArray(file.drawings) ? file.drawings.filter(isObj) : [];
  if (!Array.isArray(file.drawings)) warnings.push("The file does not list its drawings, so they can only be matched by sheet id.");
  return { ok: true, file: { ...file, drawings }, warnings };
}

// ── drawings: which of the file's drawings is which of the revision's ───────
const key = (v) => String(v ?? "").toLowerCase().replace(/\.[a-z0-9]{2,4}$/, "").replace(/[^a-z0-9]+/g, "");
const namesOf = (d) => [d.number, d.name, d.fileName, d.SheetNumber, d.SheetName, d.FileName].map(key).filter(Boolean);

/**
 * For each drawing in the file, the target sheet it most likely is.
 *   -> [{ drawing, targetSheetId (or null), by: 'hash' | 'name' | null, candidates: [targetSheetId] }]
 * A SHA-256 match is exact. Otherwise a drawing matches only when exactly one unused sheet shares its number, name
 * or file name — ambiguity leaves it unmatched (with the candidates) for the person to pick. A sheet is used once.
 */
export function matchDrawings(fileDrawings = [], targetSheets = []) {
  const used = new Set();
  const out = fileDrawings.map(d => ({ drawing: d, targetSheetId: null, by: null, candidates: [] }));
  // pass 1: identical files
  out.forEach(m => {
    if (!m.drawing.fileHash) return;
    const hits = targetSheets.filter(s => s.FileHash && s.FileHash === m.drawing.fileHash && !used.has(s.SheetID));
    m.candidates = hits.map(s => s.SheetID);
    if (hits.length === 1) { m.targetSheetId = hits[0].SheetID; m.by = "hash"; used.add(hits[0].SheetID); }
  });
  // pass 2: the same name
  out.forEach(m => {
    if (m.targetSheetId != null) return;
    const mine = new Set(namesOf(m.drawing));
    const hits = targetSheets.filter(s => !used.has(s.SheetID) && namesOf(s).some(n => mine.has(n)));
    m.candidates = [...new Set([...m.candidates, ...hits.map(s => s.SheetID)])];
    if (hits.length === 1) { m.targetSheetId = hits[0].SheetID; m.by = "name"; used.add(hits[0].SheetID); }
  });
  return out;
}

// ── prepare the document for the database ────────────────────────────────────
/**
 * The document to send and a report of what the import will do.
 *   mapping: { [fileSheetId]: targetSheetId | null }  — null / missing = skip that drawing
 *   targetRevisionId: the revision being imported into
 * -> { document, report }
 */
export function prepareImport({ file, mapping = {}, targetRevisionId = null }) {
  const t = file.takeoff;
  const sameRevision = file.source?.revisionId != null && targetRevisionId != null && file.source.revisionId === targetRevisionId;
  const mapSheet = (id) => (id == null ? { keep: true, id: null } : (mapping[id] != null ? { keep: true, id: mapping[id] } : { keep: false, id: null }));
  const report = { rooms: 0, roomsDropped: 0, conditions: 0, conditionsDropped: 0, overrides: 0, overridesDropped: 0, backOutsDropped: 0, sheetsMatched: 0, sheetsSkipped: 0, zonesCleared: 0, droppedBySheet: {} };
  const bump = (id) => { report.droppedBySheet[id] = (report.droppedBySheet[id] || 0) + 1; };

  // rooms: renumber to ids that cannot exist (negative), mapping sheets, clearing source-only references
  const roomMap = new Map();
  const rooms = [];
  for (const r of t.rooms) {
    const m = mapSheet(r.SheetID);
    if (!m.keep) { report.roomsDropped += 1; bump(r.SheetID); continue; }
    const id = -(rooms.length + 1);
    roomMap.set(r.RoomID, id);
    let zone = r.AHUZoneID ?? null;
    if (zone != null && !sameRevision) { zone = null; report.zonesCleared += 1; }
    rooms.push({ ...r, RoomID: id, SheetID: m.id, AHUZoneID: zone });
  }
  report.rooms = rooms.length;

  const conditions = [];
  for (const c of t.conditions) {
    if (c.MeasureRole === "deduct") { report.backOutsDropped += 1; continue; }
    const m = mapSheet(c.SheetID);
    if (!m.keep) { report.conditionsDropped += 1; bump(c.SheetID); continue; }
    if (c.RoomID != null && !roomMap.has(c.RoomID)) { report.conditionsDropped += 1; continue; }       // its room was dropped
    const roomB = c.RoomBID != null ? (roomMap.get(c.RoomBID) ?? null) : null;
    conditions.push({ ...c, SheetID: m.id, RoomID: c.RoomID != null ? roomMap.get(c.RoomID) : null, RoomBID: roomB });
  }
  report.conditions = conditions.length;

  const overrides = [];
  for (const o of t.overrides) {
    if (!roomMap.has(o.RoomAID) || !roomMap.has(o.RoomBID)) { report.overridesDropped += 1; continue; }
    overrides.push({ ...o, RoomAID: roomMap.get(o.RoomAID), RoomBID: roomMap.get(o.RoomBID) });
  }
  report.overrides = overrides.length;

  // drawing setup (scale, position, page) only for the drawings that were mapped
  const sheets = [];
  for (const s of t.sheets) {
    const m = mapSheet(s.SheetID);
    if (m.keep && m.id != null) { sheets.push({ ...s, SheetID: m.id }); report.sheetsMatched += 1; } else report.sheetsSkipped += 1;
  }
  return { document: { rooms, conditions, overrides, sheets }, report };
}

/** The lines the import dialog shows under "What will happen". */
export function describeImport(report, { targetRoomCount = 0, targetConditionCount = 0 } = {}) {
  const lines = [`${report.rooms} room${report.rooms === 1 ? "" : "s"}, ${report.conditions} drawn condition${report.conditions === 1 ? "" : "s"} and ${report.overrides} edge override${report.overrides === 1 ? "" : "s"} will be imported.`];
  if (targetRoomCount || targetConditionCount) lines.push(`This REPLACES the takeoff now in this revision (${targetRoomCount} room${targetRoomCount === 1 ? "" : "s"}, ${targetConditionCount} drawn condition${targetConditionCount === 1 ? "" : "s"}). An automatic snapshot of it is saved first, so you can undo the import from Snapshots.`);
  else lines.push("This revision has no takeoff yet, so nothing is replaced.");
  if (report.sheetsMatched) lines.push(`${report.sheetsMatched} drawing${report.sheetsMatched === 1 ? "" : "s"}' scale and position will be set from the file.`);
  const skipped = report.roomsDropped + report.conditionsDropped;
  if (skipped) lines.push(`${report.roomsDropped} room${report.roomsDropped === 1 ? "" : "s"} and ${report.conditionsDropped} condition${report.conditionsDropped === 1 ? "" : "s"} left out — they were on drawings you did not map, or in rooms that were left out.`);
  if (report.overridesDropped) lines.push(`${report.overridesDropped} edge override${report.overridesDropped === 1 ? "" : "s"} left out (a room they join was left out).`);
  if (report.backOutsDropped) lines.push(`${report.backOutsDropped} back-out${report.backOutsDropped === 1 ? "" : "s"} not imported — they hang off room elements, which the file does not carry.`);
  if (report.zonesCleared) lines.push(`${report.zonesCleared} AHU zone assignment${report.zonesCleared === 1 ? "" : "s"} cleared — zones belong to the revision they were made in.`);
  return lines;
}

/** Was the file made on this site? A different host can mean different catalog / template ids. */
export const sameEnvironment = (file, host) => !file?.source?.host || !host || file.source.host === host;
